import { useEffect, useRef, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useI18n } from '@/context/I18nContext';

interface Props {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  loadingLabel?: string;
  loading?: boolean;
  variant?: 'danger' | 'primary';
}

export function ConfirmDialog({
  open,
  onConfirm,
  onCancel,
  title,
  message,
  confirmLabel,
  loadingLabel,
  loading = false,
  variant = 'danger',
}: Props) {
  const { t } = useI18n();
  const ref = useRef<HTMLDialogElement>(null);
  const resolvedLabel = confirmLabel ?? t('common.delete');
  const resolvedLoadingLabel = loadingLabel ?? (variant === 'primary' ? t('common.applying') : t('common.deleting'));

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      el.showModal();
    } else if (!open && el.open) {
      el.close();
    }
  }, [open]);

  if (!open) return null;

  const isPrimary = variant === 'primary';

  return (
    <dialog
      ref={ref}
      onCancel={(e) => {
        e.preventDefault();
        if (!loading) onCancel();
      }}
      onClick={(e) => {
        if (e.target === ref.current && !loading) onCancel();
      }}
      className="
        w-full max-w-[420px]
        bg-bg-raised border border-border-subtle rounded-xl
        text-text-primary
        p-0 m-auto
      "
    >
      <div className="p-6">
        {/* Icon + Title */}
        <div className="flex items-start gap-3 mb-4">
          <div className={`flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0 ${
            isPrimary ? 'bg-accent/10' : 'bg-red/10'
          }`}>
            <AlertTriangle className={`w-5 h-5 ${isPrimary ? 'text-accent' : 'text-red'}`} />
          </div>
          <div>
            <h3 className="text-base font-semibold tracking-tight">{title}</h3>
            <p className="text-sm text-text-secondary mt-1 leading-relaxed">{message}</p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 justify-end mt-6">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-3 py-1.5 rounded-md text-xs font-medium text-text-secondary
              border border-border-default hover:border-border-hover hover:text-text-primary
              transition-colors cursor-pointer disabled:opacity-50"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`px-3 py-1.5 rounded-md text-xs font-medium text-white
              disabled:opacity-50 transition-colors cursor-pointer ${
                isPrimary
                  ? 'bg-accent hover:bg-accent-hover'
                  : 'bg-red hover:bg-red/80'
              }`}
          >
            {loading ? resolvedLoadingLabel : resolvedLabel}
          </button>
        </div>
      </div>
    </dialog>
  );
}
